// Por dónde vende la marca y cómo se conecta cada canal
// (ADR-2026-09-29-onboarding-v2-canal-de-venta). La respuesta sale de la 4ª pregunta; si es
// una plataforma, el onboarding muestra su paso de conexión justo después de la marca
// (ADR-2026-10-02-onboarding-v2-canal-despues-de-la-marca). Todo puro: la pantalla y confirmarMarca lo consultan.
//
// Para enchufar una integración nueva (Shopify, Mercado Libre): poner `disponible: true`
// en su entrada y darle su `case` en `conectar` de ConectarCanal.tsx. Nada más cambia.

import type { Reaccion, Tramo } from './dialogo';
import type { RespuestasDeLaEspera } from './marca';
import { respuestasDe } from './respuestas';
import { ONBOARDING_V2 } from '../../components/onboarding/onboardingV2Routes';

export type CanalDeVenta = 'shopify' | 'tiendanube' | 'mercadolibre' | 'ninguno' | 'otro';
export type CanalConectable = Extract<CanalDeVenta, 'shopify' | 'tiendanube' | 'mercadolibre'>;

export interface InfoDeCanal {
  nombre: string;
  /** ¿Ya se puede conectar desde Atlas? */
  disponible: boolean;
  /** Qué gana la persona al conectarlo (en los que no están disponibles, en infinitivo:
   *  van después de "Cuando esté, vas a poder:"). */
  beneficios: string[];
  /** El color de la insignia cuando no hay logo. */
  color: string;
}

export const CANALES: Record<CanalConectable, InfoDeCanal> = {
  tiendanube: {
    nombre: 'Tiendanube',
    disponible: true,
    beneficios: [
      'Traemos tus productos con sus fotos, sin cargarlos de nuevo.',
      'Subes las fotos que generes directo a tu tienda.',
    ],
    color: '#2C3357',
  },
  shopify: {
    nombre: 'Shopify',
    disponible: false,
    beneficios: [
      'Traer tus productos con sus fotos, sin cargarlos de nuevo.',
      'Subir las fotos que generes directo a tu tienda.',
    ],
    color: '#5E8E3E',
  },
  mercadolibre: {
    nombre: 'Mercado Libre',
    disponible: false,
    beneficios: [
      'Traer tus publicaciones con sus fotos.',
      'Mejorar las fotos de tus publicaciones desde Atlas.',
    ],
    color: '#FFE600',
  },
};

export function esConectable(canal: string | undefined | null): canal is CanalConectable {
  return canal === 'shopify' || canal === 'tiendanube' || canal === 'mercadolibre';
}

/**
 * Adónde va la persona después de confirmar su marca: su canal, si tiene uno para conectar,
 * y si no, las primeras piezas. El canal va justo después del análisis
 * (ADR-2026-10-02-onboarding-v2-canal-despues-de-la-marca); la competencia ya no es parte
 * del onboarding (ADR-2026-10-02-onboarding-v2-sin-competencia).
 */
export function pasoTrasLaMarca(respuestas: RespuestasDeLaEspera | undefined): string {
  return canalParaConectar(respuestas) ? ONBOARDING_V2.setupCanal : ONBOARDING_V2.setupPiezas;
}

/**
 * Qué canal se ofrece conectar, entre todos los que marcó
 * (ADR-2026-10-02-onboarding-v2-preguntas-de-varias): el primero que se puede conectar hoy
 * (Tiendanube); si ninguno, el primero conectable (muestra "muy pronto"). Null: nada que conectar.
 */
export function canalParaConectar(respuestas: RespuestasDeLaEspera | undefined): CanalConectable | null {
  const conectables = respuestasDe(respuestas, 'canal').filter(esConectable);
  return conectables.find((c) => CANALES[c].disponible) ?? conectables[0] ?? null;
}

/**
 * Los otros canales que marcó y todavía no se pueden conectar (Shopify, Mercado Libre): el
 * paso del canal avisa que se le va a avisar. Sin el que ya se muestra en el paso.
 */
export function canalesParaAvisar(respuestas: RespuestasDeLaEspera | undefined, mostrado: CanalConectable | null): CanalConectable[] {
  return respuestasDe(respuestas, 'canal')
    .filter(esConectable)
    .filter((c) => c !== mostrado && !CANALES[c].disponible);
}

/**
 * El texto de ese aviso, para 1, 2 o más canales. `tambien`: el canal del paso tampoco se
 * puede conectar todavía (el paso ya dice "muy pronto").
 */
export function avisoDeCanalesPronto(canales: readonly CanalConectable[], tambien: boolean): string {
  const nombres = canales.map((c) => CANALES[c].nombre);
  const lista = nombres.length <= 1 ? (nombres[0] ?? '') : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
  const varios = nombres.length > 1;
  const cuando = varios ? 'apenas estén disponibles' : 'apenas esté disponible';
  return tambien
    ? `${lista} ${varios ? 'tampoco se pueden' : 'tampoco se puede'} conectar todavía desde Atlas. Te avisamos ${cuando}.`
    : `${lista} todavía no ${varios ? 'se pueden' : 'se puede'} conectar desde Atlas. Te avisamos ${cuando}.`;
}

/** Lo que recuerda el chat del agente (memoria `tienda`). Null si no hay nada que decir. */
export function memoriaDeCanal(canal: string | undefined, otro: string | undefined): string | null {
  if (esConectable(canal)) return `Vende por ${CANALES[canal].nombre}.`;
  if (canal === 'ninguno') return 'Todavía no vende por una tienda online ni por un marketplace.';
  if (canal === 'otro' && otro?.trim()) return `Vende por: ${otro.trim()}.`;
  return null;
}

// ─── Lo que dice Cumbre en el paso del canal ─────────────────────────────────────────

/** El globo del paso, según si ya está conectada y si el canal se puede conectar hoy. */
export function dialogoDelCanal(p: { canal: CanalConectable; conectado: boolean; tienda: string | null }): Tramo[] {
  const { nombre, disponible } = CANALES[p.canal];
  if (p.conectado) {
    return [
      { texto: '¡Conectada!', estilo: 'saludo' },
      { texto: `${p.tienda || 'Tu tienda'} ya está en Atlas`, estilo: 'pregunta' },
      { texto: 'Traigo tus productos con sus fotos.', estilo: 'detalle' },
    ];
  }
  if (!disponible) {
    return [
      { texto: `Vi que vendes por ${nombre}.`, estilo: 'saludo' },
      { texto: 'Muy pronto vas a poder conectarla', estilo: 'pregunta' },
      { texto: `Ya quedó anotado: apenas se pueda conectar ${nombre} desde Atlas, te aviso.`, estilo: 'detalle' },
    ];
  }
  return [
    { texto: `Vi que vendes por ${nombre}.`, estilo: 'saludo' },
    { texto: '¿Conectamos tu tienda?', estilo: 'pregunta' },
    { texto: 'Traigo tus productos con sus fotos, y lo que crees en Atlas va directo a tu tienda.', estilo: 'detalle' },
  ];
}

/** Mientras se autoriza en la ventana de la plataforma. */
export function reaccionConectando(canal: CanalConectable): Reaccion {
  return { id: `conectando-${canal}`, texto: `Te abrí una ventana de ${CANALES[canal].nombre}. Autoriza la conexión ahí.` };
}

/** Lo que trajo el catálogo al conectar (`traerCatalogoAlConectar`). */
export function reaccionAlCatalogo(r: { encontrados: number; enviados: number; fallo: boolean }): Reaccion {
  const texto =
    r.enviados > 0 && !r.fallo
      ? `Listo: traje ${r.enviados} ${r.enviados === 1 ? 'producto' : 'productos'} con sus fotos. Están en Mi marca.`
      : r.enviados > 0
        ? `Traje ${r.enviados} de ${r.encontrados} productos. El resto lo puedes traer desde Mi marca.`
        : r.fallo
          ? 'No pude traer tus productos ahora. Los puedes traer cuando quieras desde Mi marca.'
          : 'Tus productos ya estaban en Atlas.';
  return { id: `catalogo-${r.enviados}-${r.encontrados}-${r.fallo}`, texto };
}
